import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import { useTablesData } from '../../hooks/useTablesData';
import useValidations from '../../hooks/useValidations';
import { transformTablesForHierarchy } from '../../utils/schemaHierarchy';

// Enhanced components
import ValidationErrorBoundary from './components/ValidationErrorBoundary';
import ValidationHealthDashboard from './components/ValidationHealthDashboard';
import ValidationResultsSummary from './components/ValidationResultsSummary';
import ValidationOverviewDashboard from './components/ValidationOverviewDashboard';
import ValidationRuleList from './components/ValidationRuleList';
import ValidationRuleEditor from './components/ValidationRuleEditor';
import ValidationErrorHandler from './components/ValidationErrorHandler';
import ValidationAutomationControls from './components/ValidationAutomationControls';

// Common components
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import HierarchicalDataExplorer from '../../components/common/HierarchicalDataExplorer';
import { ServerIcon, ArrowLeftIcon, ExclamationTriangleIcon, ChartBarIcon, EyeIcon, CommandLineIcon, SparklesIcon } from '@heroicons/react/24/outline';

// Services
import validationService from '../../services/validationService';
import { metadataAPI } from '../../api/enhancedApiService';

// Transform simple table names into hierarchical structure

const ValidationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeConnection, connections, setCurrentConnection } = useConnection();
  const { updateBreadcrumbs, showNotification } = useUI();

  // Local state
  const [selectedTable, setSelectedTable] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [columnsCache, setColumnsCache] = useState({});
  const [pageError, setPageError] = useState(null);
  const [sourceTypeFilter, setSourceTypeFilter] = useState('all'); // 'all', 'manual', 'dbt'

  // Connection and automation data
  const connectionId = activeConnection?.id;

  // Set breadcrumbs based on selected table
  useEffect(() => {
    try {
      if (selectedTable) {
        updateBreadcrumbs([
          { name: 'Validations', href: '/validations' },
          { name: selectedTable }
        ]);
      } else {
        updateBreadcrumbs([
          { name: 'Validations', href: '/validations' }
        ]);
      }
    } catch (error) {
      console.error('Error updating breadcrumbs:', error);
    }
  }, [updateBreadcrumbs, selectedTable]);

  // Handle table selection from URL params
  useEffect(() => {
    try {
      const tableParam = searchParams.get('table');
      if (tableParam && tableParam !== selectedTable) {
        setSelectedTable(tableParam);
      } else if (!tableParam && selectedTable) {
        setSelectedTable('');
      }
    } catch (error) {
      console.error('Error handling URL params:', error);
      setPageError('Failed to process URL parameters');
    }
  }, [searchParams, selectedTable]);

  // Handle connection_id from URL params (HC1 fix). When a user drills in
  // from a cross-connection home-page widget (e.g. TopFailingValidations),
  // the URL carries connection_id. If it doesn't match the active
  // connection, switch automatically so the data the user clicked through
  // for actually loads. Without this, the page reads from activeConnection
  // and renders empty.
  //
  // After switching, we consume the URL param so:
  //  (a) a subsequent manual switch via the connection picker isn't
  //      "bounced back" by a re-firing of this effect, and
  //  (b) the URL stays clean for sharing/bookmarks.
  useEffect(() => {
    const targetConnectionId = searchParams.get('connection_id');
    if (!targetConnectionId) return;
    // Connections may still be loading on first render; bail and re-fire
    // when the list arrives (connections.length is in deps).
    if (!connections || connections.length === 0) return;

    if (activeConnection?.id !== targetConnectionId) {
      const target = connections.find(c => c.id === targetConnectionId);
      if (target) {
        setCurrentConnection(target);
      }
    }

    // Remove connection_id from the URL once we've acted on it. Preserve
    // any other params (e.g. ?table=...).
    const next = new URLSearchParams(searchParams);
    next.delete('connection_id');
    setSearchParams(next, { replace: true });
  }, [searchParams, activeConnection?.id, connections.length, setCurrentConnection, setSearchParams]);

  // Enhanced data fetching with error handling
  const tablesQuery = useTablesData(connectionId, {
    enabled: !!connectionId,
    onError: (error) => {
      console.error('Error loading tables:', error);
      showNotification('Failed to load tables list', 'error');
    }
  });

  const validationData = useValidations(connectionId, selectedTable);

  // Enhanced column preloading with error handling
  const preloadTableColumns = useCallback(async (tableName) => {
    if (!connectionId || !tableName || columnsCache[tableName]) return;

    try {
      console.log(`[ValidationPage] Preloading columns for table: ${tableName}`);

      // Use tiered metadata API
      const response = await metadataAPI.getTableColumnsTiered(
        connectionId,
        tableName,
        null, // schema (we don't have it in this context)
        { forceFresh: false }
      );

      // Handle different possible response structures
      let columnsData = [];
      if (response?.columns && Array.isArray(response.columns)) {
        columnsData = response.columns;
      } else if (response?.data?.columns && Array.isArray(response.data.columns)) {
        columnsData = response.data.columns;
      } else if (Array.isArray(response)) {
        columnsData = response;
      }

      // Transform columns to match expected format
      const transformedColumns = columnsData.map(col => ({
        name: col.name,
        data_type: col.data_type || col.type,
        is_nullable: col.is_nullable !== undefined ? col.is_nullable : col.nullable,
        // Preserve all original fields
        ...col
      }));

      // Update cache
      setColumnsCache(prev => ({
        ...prev,
        [tableName]: transformedColumns
      }));

      console.log(`[ValidationPage] Cached ${transformedColumns.length} columns for ${tableName}`);
    } catch (error) {
      console.error(`[ValidationPage] Error preloading columns for ${tableName}:`, error);
      // Don't show notification for column preloading errors as they're not critical
    }
  }, [connectionId, columnsCache]);

  // Preload columns when table is selected
  useEffect(() => {
    if (selectedTable) {
      preloadTableColumns(selectedTable);
    }
  }, [selectedTable, preloadTableColumns]);

  // Enhanced table selection with error handling
  const handleTableSelect = useCallback((tableOrObject) => {
    try {
      // Handle both string table names and object structures
      // IMPORTANT: Use full_name first for multi-database support
      const tableName = typeof tableOrObject === 'string' ? tableOrObject : tableOrObject.full_name || tableOrObject.object_name;
      setSelectedTable(tableName);
      setSearchParams({ table: tableName });
      preloadTableColumns(tableName);

      // Reset error states when changing tables
      setValidationErrors(null);
      setPageError(null);
    } catch (error) {
      console.error('Error selecting table:', error);
      showNotification('Failed to select table', 'error');
    }
  }, [setSearchParams, preloadTableColumns, showNotification]);

  // Enhanced back to overview handler
  const handleBackToOverview = useCallback(() => {
    try {
      setSelectedTable('');
      setSearchParams({});
      // Reset all table-specific state
      setShowEditor(false);
      setEditingRule(null);
      setValidationErrors(null);
      setPageError(null);
    } catch (error) {
      console.error('Error returning to overview:', error);
      showNotification('Failed to return to overview', 'error');
    }
  }, [setSearchParams, showNotification]);

  // Extract validations with errors with safety checks
  const errorValidations = useMemo(() => {
    try {
      if (!validationData.validations) return [];
      return validationData.validations.filter(v => v.error);
    } catch (error) {
      console.error('Error filtering error validations:', error);
      return [];
    }
  }, [validationData.validations]);

  // Set validation errors when they occur
  useEffect(() => {
    if (errorValidations.length > 0) {
      setValidationErrors(errorValidations);
    } else {
      setValidationErrors(null);
    }
  }, [errorValidations]);

  // Enhanced validation generation handler
  const handleGenerateValidations = useCallback(async () => {
    if (!connectionId || !selectedTable) return;

    try {
      showNotification('Generating default validations...', 'info');

      await validationService.generateDefaultValidations(connectionId, selectedTable);

      // Reload validations after generation
      await validationData.loadValidations(true);

      showNotification('Default validations generated successfully', 'success');
    } catch (err) {
      console.error('Error generating validations:', err);
      showNotification(`Failed to generate validations: ${err.message}`, 'error');
    }
  }, [connectionId, selectedTable, validationData, showNotification]);

  // Enhanced validation save handler
  const handleSaveValidation = useCallback(() => {
    try {
      // First close the editor immediately
      setShowEditor(false);
      setEditingRule(null);

      // Then load validations in the background
      validationData.loadValidations(true).catch(err => {
        console.error('Error reloading validations:', err);
        showNotification('Rule was saved, but there was an error refreshing the list', 'warning');
      });
    } catch (error) {
      console.error('Error in save validation handler:', error);
      showNotification('Error processing validation save', 'error');
    }
  }, [validationData, showNotification]);

  // Enhanced run all validations handler
  const handleRunAll = useCallback(async () => {
    try {
      showNotification('Running validations...', 'info');

      // Run all validations
      await validationData.runAllValidations();

      // Force refresh validations data
      await validationData.loadValidations(true);

      showNotification('Validations completed successfully', 'success');
    } catch (err) {
      console.error('Error running validations:', err);
      showNotification(`Failed to run validations: ${err.message}`, 'error');
    }
  }, [validationData, showNotification]);

  // Enhanced optimistic UI updates handler
  const handleUpdateValidations = useCallback((updatedValidations) => {
    try {
      if (!validationData) return;

      // Update the validations state using the hook's setter
      validationData.setValidations(updatedValidations);

      // Update metrics using the hook's setter
      if (validationData.setMetrics) {
        const updatedMetrics = validationService.calculateMetrics(updatedValidations);
        validationData.setMetrics(updatedMetrics);
      }
    } catch (error) {
      console.error('Error updating validations:', error);
    }
  }, [validationData]);

  // Show page-level error if one occurred
  if (pageError) {
    return (
      <div className="py-4">
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-danger-400 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-danger-800">Page Error</h3>
              <p className="mt-1 text-danger-700">{pageError}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 px-4 py-2 bg-danger-100 text-danger-800 rounded-md hover:bg-danger-200"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If no connections, show empty state
  if (!activeConnection) {
    return (
      <EmptyState
        icon={ServerIcon}
        title="No connections configured"
        description="Create a connection to start defining validation rules"
        actionText="Add Connection"
        actionLink="/connections/new"
      />
    );
  }

  return (
    <ValidationErrorBoundary
      tableName={selectedTable}
      connectionId={connectionId}
    >
      <div className="py-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            {/* Back button when viewing a specific table */}
            {selectedTable && (
              <button
                onClick={handleBackToOverview}
                className="mr-4 inline-flex items-center px-3 py-2 border border-secondary-300 shadow-sm text-sm leading-4 font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <ArrowLeftIcon className="-ml-0.5 mr-2 h-4 w-4" />
                Back to Overview
              </button>
            )}

            <div>
              <h1 className="text-2xl font-semibold text-secondary-900">
                {selectedTable ? `Validations: ${selectedTable}` : 'Data Validations'}
              </h1>
              {selectedTable && (
                <p className="text-sm text-gray-500 mt-1 mb-3">
                  Manage validation rules and monitor data quality for this object
                </p>
              )}
            </div>
          </div>

          {/* Cross-page navigation links - right aligned */}
          {selectedTable && connectionId && activeConnection?.slug && (
            <div className="flex items-center space-x-3">
              <Link
                to={`/explorer/${activeConnection.slug}/tables/${encodeURIComponent(selectedTable)}`}
                className="inline-flex items-center px-4 py-2 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                title="View detailed information about this table"
              >
                <EyeIcon className="-ml-1 mr-2 h-5 w-5 text-secondary-500" aria-hidden="true" />
                View in Data Explorer
              </Link>

              <Link
                to={`/metadata?table=${encodeURIComponent(selectedTable)}&connection=${connectionId}`}
                className="inline-flex items-center px-4 py-2 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                title="View metadata information for this table"
              >
                <CommandLineIcon className="-ml-1 mr-2 h-5 w-5 text-secondary-500" aria-hidden="true" />
                View Metadata
              </Link>
            </div>
          )}
        </div>

        {/* Breadcrumb-style navigation */}
        {selectedTable && (
          <div className="mt-2 text-sm text-secondary-500">
            <button
              onClick={handleBackToOverview}
              className="text-primary-600 hover:text-primary-700 hover:underline"
            >
              All Tables
            </button>
            <span className="mx-2">›</span>
            <span>{selectedTable}</span>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Side panel with tables and automation controls */}
          <div className="lg:col-span-3 space-y-6">
            {/* Hierarchical Data Explorer - moved to top */}
            {tablesQuery.isLoading ? (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="md" />
                </div>
                <p className="text-center text-sm text-gray-500 mt-2">Loading data structure...</p>
              </div>
            ) : tablesQuery.isError ? (
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-center py-4">
                  <p className="text-sm text-danger-500">Error loading data structure</p>
                  <button
                    onClick={() => tablesQuery.refetch()}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-500"
                  >
                    Try again
                  </button>
                </div>
              </div>
            ) : (
              <HierarchicalDataExplorer
                data={transformTablesForHierarchy(tablesQuery.data?.tables || [], activeConnection)}
                onItemSelect={handleTableSelect}
                selectedItem={selectedTable}
                title="Data Objects"
                showCounts={true}
                renderItemBadges={(item) => (
                  <>
                    {item.validations_count > 0 && (
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">
                        {item.validations_count} rules
                      </span>
                    )}
                  </>
                )}
                renderItemActions={(item) => (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Handle quick actions like viewing validations summary
                      }}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="View validation summary"
                    >
                      <ChartBarIcon className="h-4 w-4" />
                    </button>
                  </>
                )}
              />
            )}

            {/* Validation Automation Controls - moved below data objects */}
            <ValidationErrorBoundary>
              <ValidationAutomationControls
                connectionId={connectionId}
                tableName={selectedTable}
              />
            </ValidationErrorBoundary>
          </div>

          {/* Main content with validations */}
          <div className="lg:col-span-9">
            {!selectedTable ? (
              /* Show overview dashboard when no table is selected */
              <ValidationErrorBoundary>
                <ValidationOverviewDashboard
                  connectionId={connectionId}
                  onTableSelect={handleTableSelect}
                />
              </ValidationErrorBoundary>
            ) : showEditor ? (
              <ValidationErrorBoundary tableName={selectedTable}>
                <ValidationRuleEditor
                  connectionId={connectionId}
                  tableName={selectedTable}
                  rule={editingRule}
                  cachedColumns={columnsCache[selectedTable] || []}
                  onSave={handleSaveValidation}
                  onCancel={() => {
                    setShowEditor(false);
                    setEditingRule(null);
                  }}
                />
              </ValidationErrorBoundary>
            ) : (
              <div className="space-y-6">
                {/* Results Summary */}
                {selectedTable && (
                  <ValidationErrorBoundary>
                    <ValidationResultsSummary
                      metrics={validationData.metrics}
                      connectionId={connectionId}
                      tableName={selectedTable}
                      onRunAll={handleRunAll}
                      isRunning={validationData.runningValidation}
                      isLoading={validationData.loading}
                      validations={validationData.validations || []}
                    />
                  </ValidationErrorBoundary>
                )}

                {/* Enhanced Health Dashboard with Error Boundary */}
                {selectedTable && (
                  <ValidationErrorBoundary tableName={selectedTable}>
                    <ValidationHealthDashboard
                      connectionId={connectionId}
                      tableName={selectedTable}
                      days={30}
                    />
                  </ValidationErrorBoundary>
                )}

                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 py-3 border-b border-secondary-200 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <h3 className="text-lg font-medium text-secondary-900">
                        Validation Rules: {selectedTable}
                      </h3>

                      {/* Source Type Filter */}
                      <select
                        value={sourceTypeFilter}
                        onChange={(e) => {
                          setSourceTypeFilter(e.target.value);
                          validationData.loadValidations(true, e.target.value === 'all' ? null : e.target.value);
                        }}
                        className="text-sm border border-secondary-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="all">All Sources</option>
                        <option value="manual">Manual</option>
                        <option value="auto_generated">Auto-generated (Live)</option>
                        <option value="auto_cached">Auto-cached (Profiled)</option>
                        <option value="dbt">dbt</option>
                      </select>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleGenerateValidations}
                        className="inline-flex items-center px-3 py-1.5 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500"
                      >
                        Generate Quality Checks
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setEditingRule(null);
                          setShowEditor(true);
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        New Rule
                      </button>
                    </div>
                  </div>

                  {/* Display validation errors at the top if present */}
                  {validationErrors && validationErrors.length > 0 && (
                    <div className="px-4 py-3 bg-white">
                      <ValidationErrorHandler
                        errors={validationErrors}
                        onRefresh={validationData.runAllValidations}
                        connectionId={connectionId}
                        tableName={selectedTable}
                      />
                    </div>
                  )}

                  {/* Enhanced Validation rule list with error boundary */}
                  <ValidationErrorBoundary tableName={selectedTable}>
                    <ValidationRuleList
                      validations={validationData.validations}
                      isLoading={validationData.loading}
                      onEdit={(rule) => {
                        setEditingRule(rule);
                        setShowEditor(true);
                      }}
                      onRefreshList={() => validationData.loadValidations(true)}
                      onUpdateValidations={handleUpdateValidations}
                      tableName={selectedTable}
                      connectionId={connectionId}
                      onRunSingle={validationData.runSingleValidation}
                      isRunningValidation={validationData.runningValidation}
                      runningRuleId={validationData.runningRuleId}
                      showNotification={showNotification}
                    />
                  </ValidationErrorBoundary>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </ValidationErrorBoundary>
  );
};

export default ValidationPage;